// Hook personalizado para gestionar órdenes de compra en el panel de administración
// Maneja la paginación, búsqueda y estado de la lista de órdenes

import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { obtenerOrdenesPaginadas } from '../services/ordenesService';

/**
 * Hook para obtener y gestionar órdenes con paginación
 * @param {object} options - Opciones adicionales del query
 * @returns {object} - Estado y funciones para gestionar órdenes
 */
export const useOrders = (options = {}) => {
  // Estado local para paginación y búsqueda
  const [offset, setOffset] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');
  const [orderBy, setOrderBy] = useState('-created_at');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Función para actualizar búsqueda con debounce
  const handleSearchChange = useCallback((value) => {
    setSearchText(value);
    // Resetear offset cuando cambia la búsqueda
    setOffset(0);
    
    // Debounce simple de 500ms
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(value);
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, []);

  // Función para cambiar filtro de estado
  const handleEstadoChange = useCallback((value) => {
    setEstadoFilter(value);
    setOffset(0); // Resetear offset al cambiar filtro
  }, []);

  // Query para obtener órdenes paginadas
  const ordersQuery = useQuery({
    queryKey: ['adminOrders', offset, debouncedSearch, estadoFilter, orderBy],
    queryFn: () => obtenerOrdenesPaginadas({
      limit: 50,
      offset,
      searchText: debouncedSearch || null,
      estado: estadoFilter || null,
      orderBy
    }),
    staleTime: 1000 * 60 * 2, // 2 minutos
    gcTime: 1000 * 60 * 5, // 5 minutos
    refetchOnWindowFocus: false,
    retry: 1,
    ...options
  });

  // Función para cargar la siguiente página
  const loadNextPage = useCallback(() => {
    if (ordersQuery.data?.nextOffset !== null && !ordersQuery.isFetching) {
      setOffset(ordersQuery.data.nextOffset);
    }
  }, [ordersQuery.data?.nextOffset, ordersQuery.isFetching]);

  // Función para resetear a la primera página
  const resetToFirstPage = useCallback(() => {
    setOffset(0);
  }, []);

  // Función para cambiar el ordenamiento
  const handleOrderByChange = useCallback((newOrderBy) => {
    setOrderBy(newOrderBy);
    setOffset(0); // Resetear offset al cambiar ordenamiento
  }, []);

  // Función para refrescar los datos
  const refresh = useCallback(() => {
    setOffset(0);
    setSearchText('');
    setEstadoFilter('');
    setDebouncedSearch('');
    setOrderBy('-created_at');
    ordersQuery.refetch();
  }, [ordersQuery]);

  return {
    // Estado del query
    ordersQuery,
    orders: ordersQuery.data?.orders || [],
    isLoading: ordersQuery.isLoading,
    isError: ordersQuery.isError,
    error: ordersQuery.error,
    
    // Estado de paginación
    hasNextPage: ordersQuery.data?.hasMore || false,
    totalCount: ordersQuery.data?.totalCount || 0,
    currentOffset: offset,
    
    // Estado de búsqueda
    searchText,
    debouncedSearch,
    estadoFilter,
    orderBy,
    
    // Funciones de control
    loadNextPage,
    resetToFirstPage,
    handleSearchChange,
    handleEstadoChange,
    handleOrderByChange,
    setOffset,
    refresh
  };
};
